import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, Sparkles, ArrowLeft, HandHeart, FileDown, Lock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";
import AttendWithMeModal from "../components/AttendWithMeModal";
import ChecklistExportModal from "../components/ChecklistExportModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  try {
    let s = localStorage.getItem("soarlife-chat-sid");
    if (!s) {
      s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("soarlife-chat-sid", s);
    }
    return s;
  } catch {
    return `s_${Date.now()}`;
  }
}

function getClientToken() {
  try {
    return localStorage.getItem("soarlife-client-token");
  } catch {
    return null;
  }
}

const SUGGESTIONS = [
  "What's the difference between IVF and IUI?",
  "How do I prepare for my first fertility appointment?",
  "Èske w ka ede m an Kreyòl?",
  "What services does Soarlife offer?",
  "I'm nervous about my upcoming visit",
  "Tell me about Haitian Creole interpretation",
];

function ChatGate({ access }) {
  return (
    <div
      className="min-h-screen bg-[#F8F7F3] flex items-center justify-center p-5"
      data-testid="chat-page-gate"
    >
      <div className="w-full max-w-md bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl p-8 text-center shadow-xl">
        <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-5">
          <Lock size={20} className="text-[#AE8F78]" />
        </div>
        <div className="font-serif italic text-3xl text-[#3F5043] mb-2">
          Soarlife AI is for clients
        </div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed mb-6">
          {access === "locked"
            ? "Your account is registered — one last step. Enter the access code your Soarlife nurse gave you in the Client Portal to unlock the AI concierge."
            : "The AI concierge is a private companion for active Soarlife clients. Sign in with your client account, or become a client to receive your personal access code."}
        </p>
        <div className="flex flex-col gap-2">
          <Link
            to="/community"
            data-testid="chat-gate-portal"
            className="inline-flex items-center justify-center rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
          >
            {access === "locked" ? "Enter my access code" : "Sign in to Client Portal"}
          </Link>
          {access !== "locked" && (
            <Link
              to="/book"
              data-testid="chat-gate-book"
              className="inline-flex items-center justify-center rounded-full px-6 py-3 border border-[#3F5043]/30 text-[#3F5043] text-sm font-medium hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
            >
              Become a client — Book a consultation
            </Link>
          )}
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.2em] text-[#899480] hover:text-[#3F5043] mt-2"
          >
            ← Back to Soarlife
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Chat() {
  const [access, setAccess] = useState("checking"); // checking | anon | locked | allowed
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm **Soarlife AI** — your gentle companion for the fertility journey. Ask me anything about IVF, IUI, preparing for appointments, or how our nurse-led team can support you.\n\nI speak **English** and **Haitian Creole (Kreyòl Ayisyen)**. How can I walk with you today? 💛",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attendOpen, setAttendOpen] = useState(false);
  const [checklistExport, setChecklistExport] = useState(null);
  const scrollRef = useRef(null);
  const sidRef = useRef(getSessionId());

  // Gate: Soarlife AI is for active clients (access code activated)
  useEffect(() => {
    (async () => {
      try {
        const t = getClientToken();
        const res = await axios.get(`${API}/clients/me`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {},
          withCredentials: true,
        });
        setAccess(res.data?.access_granted ? "allowed" : "locked");
      } catch {
        setAccess("anon");
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: msg },
      { role: "assistant", content: "", streaming: true },
    ]);
    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const t = getClientToken();
      if (t) headers.Authorization = `Bearer ${t}`;
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ session_id: sidRef.current, message: msg }),
      });
      if (res.status === 401 || res.status === 403) {
        setAccess(res.status === 401 ? "anon" : "locked");
        setLoading(false);
        return;
      }
      if (res.status === 429) {
        const j = await res.json().catch(() => ({}));
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              j.detail ||
              "You've reached the chat limit. Please text (844) 663-2125 for personal support.",
          };
          return copy;
        });
        setLoading(false);
        return;
      }
      if (!res.ok || !res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let accum = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const data = p.slice(6);
          if (data === "[DONE]") continue;
          if (data.startsWith("[ERROR]")) continue;
          accum += data.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: accum, streaming: true };
            return copy;
          });
        }
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: accum };
        return copy;
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content:
            "I couldn't reach the chat service. Please try again, or text (844) 663-2125.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  if (access === "checking") {
    return (
      <div className="min-h-screen bg-[#F8F7F3] flex items-center justify-center" data-testid="chat-page-loading">
        <Loader2 size={22} className="animate-spin text-[#899480]" />
      </div>
    );
  }
  if (access !== "allowed") return <ChatGate access={access} />;

  return (
    <div className="min-h-screen bg-[#F8F7F3] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="bg-[#F8F7F3]/90 backdrop-blur-xl border-b border-[#BCB3A6]/40 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#3F5043]/80 hover:text-[#3F5043]"
            data-testid="chat-back-home"
          >
            <ArrowLeft size={16} /> Soarlife
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#3F5043] text-[#AE8F78] flex items-center justify-center">
              <Sparkles size={14} />
            </div>
            <div className="leading-tight">
              <div className="font-serif italic text-xl text-[#3F5043]">
                Soarlife AI
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#899480] font-semibold">
                EN · Kreyòl · Non-medical
              </div>
            </div>
          </div>
          <button
            onClick={() => setAttendOpen(true)}
            data-testid="chat-page-attend"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#875F45] text-[#F8F7F3] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#3F5043] transition-colors"
          >
            <HandHeart size={12} /> Attend With Me
          </button>
        </div>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-3xl mx-auto px-5 py-8 space-y-5">
          {messages.map((m, i) => {
            const isChecklist =
              m.role === "assistant" &&
              !m.streaming &&
              /##\s*Your Prep Checklist/i.test(m.content);
            return (
              <div key={i} className="space-y-2">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-page-msg-${m.role}`}
                >
                  <div
                    className={`max-w-[85%] px-5 py-4 rounded-3xl text-sm sm:text-base leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-md whitespace-pre-wrap"
                        : "bg-[#EDEDEE] text-[#3F5043] rounded-bl-md chat-markdown"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      m.content ? (
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      ) : m.streaming ? (
                        <span className="inline-flex gap-1 items-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#899480] animate-bounce" />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#899480] animate-bounce" style={{ animationDelay: "120ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#899480] animate-bounce" style={{ animationDelay: "240ms" }} />
                        </span>
                      ) : null
                    ) : (
                      m.content
                    )}
                  </div>
                </motion.div>
                {isChecklist && (
                  <div className="flex justify-start pl-1">
                    <button
                      data-testid="chat-page-export-pdf"
                      onClick={() => setChecklistExport({ content: m.content, title: "Your Fertility Appointment Prep" })}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#875F45] transition-colors"
                    >
                      <FileDown size={13} /> Email me as PDF
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {messages.length === 1 && (
            <div className="pt-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3">
                Try asking
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="chat-page-suggestion"
                    className="text-sm text-left px-4 py-2.5 rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 text-[#3F5043] hover:border-[#899480] hover:bg-[#EDEDEE] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="bg-[#F8F7F3] border-t border-[#BCB3A6]/40 sticky bottom-0">
        <div className="max-w-3xl mx-auto px-5 py-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-end gap-2"
          >
            <textarea
              data-testid="chat-page-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Ask about IVF, IUI, your appointment… (Enter to send)"
              className="flex-1 resize-none bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm sm:text-base text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] max-h-40"
            />
            <button
              type="submit"
              data-testid="chat-page-send"
              disabled={loading || !input.trim()}
              className="w-12 h-12 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45] transition-colors disabled:opacity-50 flex-shrink-0"
              aria-label="Send"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
          <div className="mt-2 flex items-center justify-between text-[10px] text-[#3F5043]/55">
            <div>Educational only — not medical advice.</div>
            <button
              onClick={() => setAttendOpen(true)}
              className="sm:hidden inline-flex items-center gap-1 uppercase tracking-[0.18em] font-semibold text-[#875F45] hover:text-[#3F5043]"
            >
              <HandHeart size={10} /> Attend With Me
            </button>
          </div>
        </div>
      </div>

      <AttendWithMeModal open={attendOpen} onClose={() => setAttendOpen(false)} />
      <ChecklistExportModal
        open={!!checklistExport}
        onClose={() => setChecklistExport(null)}
        content={checklistExport?.content || ""}
        defaultTitle={checklistExport?.title}
        sessionId={sidRef.current}
      />
    </div>
  );
}
